/**
 * Business Card Import Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as bizcardService from '../api/services/bizcard.service';
import type { BizcardImportRequest, BizcardConfirmRequest } from '../types';
import { toast } from '../stores/toast.store';

// Query keys
const KEYS = {
  categories: ['bizcard', 'categories'] as const,
  partners: ['bizcard', 'partners'] as const,
};

/**
 * Hook for fetching partner categories
 */
export function usePartnerCategories() {
  return useQuery({
    queryKey: KEYS.categories,
    queryFn: async () => {
      const result = await bizcardService.getPartnerCategories();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch categories');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching recent bizcard partners
 */
export function useBizcardPartners(limit?: number) {
  return useQuery({
    queryKey: [...KEYS.partners, limit],
    queryFn: async () => {
      const result = await bizcardService.getBizcardPartners(limit);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch partners');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for OCR scanning business card (does not write to DB)
 */
export function useImportBizcard() {
  return useMutation({
    mutationFn: async (data: BizcardImportRequest) => {
      const result = await bizcardService.importBizcard(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to scan bizcard');
      }
      return result.data!;
    },
    onError: (error: Error) => {
      toast.error('識別失敗', error.message);
    },
  });
}

/**
 * Hook for confirming OCR result and writing to DB
 */
export function useConfirmBizcard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BizcardConfirmRequest) => {
      const result = await bizcardService.confirmBizcard(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to confirm bizcard');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.partners });
      const message = data.is_new
        ? `已新增聯絡人: ${data.partner_name}`
        : `已更新聯絡人: ${data.partner_name}`;
      toast.success('名片匯入成功', message);
    },
    onError: (error: Error) => {
      toast.error('匯入失敗', error.message);
    },
  });
}
